<script setup lang="ts">
import { computed } from 'vue';
import TopBarIconButton from './TopBarIconButton.vue';
import AppsMenu from './AppsMenu.vue';
import MobileAppsMenu from './MobileAppsMenu.vue';
import UserMenu from './UserMenu.vue';
import { useDashboardStore, SIDEBAR_EXPANDED_WIDTH } from '@/stores/dashboard';
import { useStudyStore } from '@/stores/study';
import { platformLinks, routeConstants, mobileApps, pumpMobileApps } from '@/utils';
import spDataLogo from '@/assets/SpData.svg';
import spDataMark from '@/assets/sp-data-icon.svg';
import pumpLogo from '@/assets/pump-logo.png';

const props = defineProps({
  /**
   * Whether the sidebar is on screen. When it is not there is nothing to collapse, so the logo
   * is inert and the brand zone keeps its full width.
   */
  hasSidebar: {
    type: Boolean,
    default: true
  }
});

const dashboardStore = useDashboardStore();
const studyStore = useStudyStore();

// PUMP studies get a second mobile menu for the PUMP companion app, alongside the SP Data one.
const isPumpStudy = computed(
  () => studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP'
);

const collapsed = computed(() => props.hasSidebar && dashboardStore.sidebarCollapsed);

// The brand zone spans exactly the sidebar column, so the logo sits directly above the column
// it collapses and the two edges stay aligned at either width.
const brandWidth = computed(() =>
  props.hasSidebar ? dashboardStore.sidebarWidth : `${SIDEBAR_EXPANDED_WIDTH}px`
);

const toggleLabel = computed(() =>
  dashboardStore.sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
);

const onBrandClick = () => {
  if (props.hasSidebar) {
    dashboardStore.toggleSidebar();
  }
};
</script>

<template>
  <header class="topbar">
    <!-- The whole logo is the sidebar toggle; the badge is just the affordance for it. -->
    <component :is="hasSidebar ? 'button' : 'div'"
      class="topbar__brand"
      :class="{
        'topbar__brand--collapsed': collapsed,
        'topbar__brand--interactive': hasSidebar
      }"
      :style="{ width: brandWidth }"
      :type="hasSidebar ? 'button' : undefined"
      :aria-label="hasSidebar ? toggleLabel : undefined"
      :aria-expanded="hasSidebar ? !dashboardStore.sidebarCollapsed : undefined"
      v-tooltip.bottom="hasSidebar ? toggleLabel : ''"
      @click="onBrandClick">
      <span class="topbar__logo-wrap">
        <img v-if="collapsed"
          :src="spDataMark"
          alt="Studypages Data"
          class="topbar__mark" />
        <img v-else
          :src="spDataLogo"
          alt="Studypages Data"
          class="topbar__logo" />
        <span v-if="hasSidebar" class="topbar__toggle-badge">
          <FontAwesomeIcon :icon="['far', collapsed ? 'chevron-right' : 'chevron-left']" />
        </span>
      </span>
    </component>

    <div class="topbar__actions">
      <div class="topbar__icons">
        <TopBarIconButton icon="book"
          label="Knowledge Base"
          :href="platformLinks.knowledgeBase" />
        <TopBarIconButton icon="cog"
          label="Account Settings"
          :to="routeConstants.ACCOUNT_SETTINGS.name" />
        <TopBarIconButton icon="circle-question"
          label="Resources and Help"
          :href="platformLinks.help" />
        <AppsMenu />
        <MobileAppsMenu :apps="mobileApps" product-name="SP Data" />
        <MobileAppsMenu v-if="isPumpStudy"
          :apps="pumpMobileApps"
          product-name="PUMP"
          :badge-logo="pumpLogo" />
        <TopBarIconButton icon="house-blank"
          label="Home"
          :href="platformLinks.spDataHome" />
      </div>

      <UserMenu />
    </div>
  </header>
</template>

<style scoped lang="scss">
@use '../../assets/variables.module' as *;
.topbar {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 64px;
  background-color: white;
  border-bottom: 1px solid hsl(220, 13%, 91%);
}
.topbar__brand {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  // Full height so the badge overhanging the logo has room; overflow still clips the wordmark
  // horizontally while the width animates.
  align-self: stretch;
  overflow: hidden;
  padding: 0 20px;
  border: none;
  background-color: transparent;
  font: inherit;
  text-align: left;
  // Mirrors the sidebar's own width transition so the two edges move as one.
  transition: width 0.2s ease, padding 0.2s ease;
}
.topbar__brand--interactive {
  cursor: pointer;
}
.topbar__brand--collapsed {
  justify-content: center;
  padding: 0 12px;
}
// Shrink-wraps the logo so the badge can anchor to its corner at either width. The badge
// overhangs into the brand zone's padding, which keeps it inside the clipped box.
.topbar__logo-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}
.topbar__logo {
  height: 40px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}
.topbar__mark {
  height: 32px;
  width: 32px;
}
.topbar__toggle-badge {
  position: absolute;
  // Sits below the wordmark's baseline rather than clipping it. The collapsed mark is square,
  // so there the badge tucks into its corner instead.
  right: -6px;
  bottom: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1px solid hsl(206, 25%, 92%);
  border-radius: 50%;
  background-color: white;
  box-shadow: 0 1px 2px hsla(0, 0%, 0%, 0.08);
  color: hsl(210, 10%, 60%);
  font-size: 8px;
  line-height: 1;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.topbar__brand--collapsed .topbar__toggle-badge {
  right: -8px;
  bottom: -4px;
}
.topbar__brand--interactive:hover .topbar__toggle-badge {
  background-color: hsl(210, 20%, 96%);
  color: hsl(210, 11%, 15%);
}
// Ring the logo rather than the full-width brand zone, which would be a very heavy outline.
.topbar__brand:focus-visible {
  outline: none;

  .topbar__logo-wrap {
    outline: 2px solid $primary-color;
    outline-offset: 4px;
    border-radius: 2px;
  }
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;
  padding-right: 20px;
}
.topbar__icons {
  display: flex;
  align-items: center;
}
</style>
